import React, { useState, useEffect } from 'react';
import {
  Card, Button, message, Space, Typography, Form, InputNumber, Select, Input, DatePicker, Row, Col, Divider
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { fetchCashRegisters, type CashRegisterDto } from '../services/cashService';
import { api } from '../services/api';
import AccountSelectModal from '../components/AccountSelectModal';

const { Text } = Typography;

interface PaymentMethod {
  id: number;
  paymentName: string;
  paymentCode: string;
}

interface SelectedAccount {
  id: number;
  accountCode: string;
  accountName: string;
  firstName: string;
  lastName: string;
  companyName?: string;
}

/** Cari tahsilat / tedarikçi ödemesi (servis fişi tahsilatı servis fişi ekranından). */
const Payment: React.FC = () => {
  const location = useLocation();
  const [form] = Form.useForm();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [cashRegisters, setCashRegisters] = useState<CashRegisterDto[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(null);
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSupplierPaymentMode = new URLSearchParams(location.search).get('mode') === 'payable';

  const loadPaymentMethodsAndRegisters = async () => {
    try {
      const [pmRes, crRes] = await Promise.all([
        api.get<PaymentMethod[]>('/paymentmethods'),
        fetchCashRegisters()
      ]);
      setPaymentMethods(pmRes.data || []);
      setCashRegisters(crRes.filter((r: CashRegisterDto) => r.isActive !== false));
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Veri yüklenirken hata');
    }
  };

  useEffect(() => {
    loadPaymentMethodsAndRegisters();
  }, []);

  // URL üzerinden ?accountId= gelirse ilgili cariyi otomatik seç
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const accountIdParam = searchParams.get('accountId');
    if (!accountIdParam) return;
    const accountId = parseInt(accountIdParam, 10);
    if (!accountId || selectedAccount) return;

    const loadAccount = async () => {
      try {
        const { data } = await api.get('/accounts/' + accountId);
        const acc: SelectedAccount = {
          id: data.id,
          accountCode: data.accountCode,
          accountName: data.accountName,
          firstName: data.firstName,
          lastName: data.lastName,
          companyName: data.companyName,
        };
        setSelectedAccount(acc);
      } catch (e: any) {
        message.error(e.response?.data?.message || 'Cari bilgisi yüklenirken hata');
      }
    };

    loadAccount();
  }, [location.search, selectedAccount]);

  useEffect(() => {
    if (paymentMethods.length > 0 && form.getFieldValue('paymentMethodId') == null) {
      form.setFieldValue('paymentMethodId', paymentMethods[0].id);
    }
    if (cashRegisters.length > 0 && form.getFieldValue('cashRegisterId') == null) {
      const def = cashRegisters.find((c) => c.isDefault) || cashRegisters[0];
      if (def) form.setFieldValue('cashRegisterId', def.id);
    }
  }, [paymentMethods, cashRegisters, form]);

  const handleAccountSelect = (account: SelectedAccount) => {
    setSelectedAccount(account);
    setAccountModalVisible(false);
  };

  const handleCariPaymentSubmit = async () => {
    if (!selectedAccount) {
      message.warning('Lütfen cari seçin');
      return;
    }
    try {
      const values = await form.validateFields();
      if (values.amount <= 0) {
        message.warning('Tutar 0\'dan büyük olmalı');
        return;
      }
      setSaving(true);
      await api.post(`/accounts/${selectedAccount.id}/payments`, {
        paymentMethodId: values.paymentMethodId,
        amount: values.amount,
        paymentDate: values.paymentDate ? values.paymentDate.toISOString() : undefined,
        receiptNo: values.receiptNo || undefined,
        description: values.description || undefined,
        cashRegisterId: values.cashRegisterId ?? undefined,
        isSupplierPayment: isSupplierPaymentMode
      });
      message.success(isSupplierPaymentMode ? 'Tedarikçi ödemesi kaydedildi' : 'Tahsilat kaydedildi');
      form.resetFields();
      setSelectedAccount(null);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ödeme kaydedilirken hata');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card title={isSupplierPaymentMode ? 'Cari ödeme (tedarikçi)' : 'Cari tahsilat'}>
        <Text type="secondary">
          {isSupplierPaymentMode
            ? 'Seçtiğiniz cariye ödeme girebilirsiniz. Kasa bakiyesi azalır, cari borcu düşer ve ödeme fişi Evraklar listesine eklenir.'
            : 'Seçtiğiniz cariden tahsilat girebilirsiniz. Kasa bakiyesi artar, cari alacağı düşer ve tahsilat fişi Evraklar listesine eklenir.'}
        </Text>
        <Divider />
        <Form form={form} layout="vertical" onFinish={handleCariPaymentSubmit} initialValues={{ paymentDate: dayjs() }}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Cari" required>
                <Button
                  type="default"
                  block
                  icon={<UserOutlined />}
                  onClick={() => setAccountModalVisible(true)}
                >
                  {selectedAccount ? `${selectedAccount.accountCode} - ${selectedAccount.accountName}` : 'Cari seç'}
                </Button>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amount" label="Tutar (₺)" rules={[{ required: true, message: 'Tutar girin' }]}>
                <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentMethodId" label="Ödeme türü" rules={[{ required: true }]}>
                <Select
                  placeholder="Seçin"
                  options={paymentMethods.map((pm) => ({ label: pm.paymentName, value: pm.id }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="paymentDate" label="Tarih">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="receiptNo" label="Makbuz no">
                <Input placeholder="Boş bırakılırsa otomatik" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cashRegisterId" label="Kasa">
                <Select
                  placeholder="Kasa seçin"
                  allowClear
                  options={cashRegisters.map((c) => ({ label: c.registerName, value: c.id }))}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Açıklama">
                <Input.TextArea rows={1} placeholder="Opsiyonel" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Space>
                <Button type="primary" htmlType="submit" loading={saving} disabled={!selectedAccount}>
                  {isSupplierPaymentMode ? 'Ödeme yap' : 'Tahsilat yap'}
                </Button>
                <Button onClick={() => { form.resetFields(); setSelectedAccount(null); }}>Temizle</Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <AccountSelectModal
        visible={accountModalVisible}
        onClose={() => setAccountModalVisible(false)}
        onSelect={handleAccountSelect}
      />
    </div>
  );
};

export default Payment;
